/**
 * Data Provider for Refine
 *
 * Implements Refine DataProvider interface to connect with GeekTime API backend.
 * Handles CRUD operations for users and transactions with proper pagination,
 * filtering, and error handling.
 *
 * Key Features:
 * - Full CRUD operations (getList, getOne, create, update, deleteOne)
 * - Custom search method for universal user search
 * - Automatic CSRF token handling via axios interceptors
 * - Pagination and filtering support
 * - Type-safe operations using generated OpenAPI types
 *
 * Requirements:
 * - 3.1-3.9: User list with pagination and filtering
 * - 5.1-5.9: User details view with transaction history
 * - 6.1-6.9: Manual points adjustment (transaction creation)
 *
 * Resources Supported:
 * - users: User management operations
 * - transactions: Transaction history and creation
 * - campaigns: Campaign management operations
 */

import type { DataProvider, LogicalFilter } from "@refinedev/core";
import { AxiosError } from "axios";
import { axiosInstance } from "../utils/axiosConfig";
import {
  UsersService,
  TransactionsService,
  CampaignsService,
  type UserListResponse,
  type UserResponse,
  type UserSearchResponse,
  type TransactionListResponse,
  type TransactionResponse,
  type TransactionCreateRequest,
  type UserUpdateRequest,
  type CampaignListResponse,
  type CampaignResponse,
  type TaskListResponse,
  type ArchivedTaskListResponse,
} from "../api-client";

/**
 * Data provider for Refine framework.
 * Implements all CRUD methods and custom search functionality.
 */
export const dataProvider = {
  /**
   * Get list of resources with pagination and filtering.
   *
   * Supports:
   * - users: Paginated user list with consent filters
   * - transactions: User transaction history (requires user_id filter)
   * - campaigns: Paginated campaign list with optional status filter
   *
   * @param params - Resource name and query parameters
   * @returns Promise with data array and total count
   */
  getList: async ({ resource, pagination, filters, meta }) => {
    try {
      if (resource === "users") {
        // Extract pagination parameters
        // Try to get current page from pagination object or URL params
        let page = (pagination as any)?.current || 1;

        // Fallback: read from URL if pagination.current is not set
        if (page === 1 && typeof window !== 'undefined') {
          const urlParams = new URLSearchParams(window.location.search);
          const currentPageParam = urlParams.get('currentPage') || urlParams.get('current');
          if (currentPageParam) {
            page = parseInt(currentPageParam, 10) || 1;
          }
        }

        const pageSize = (pagination as any)?.pageSize || 20;
        const offset = (page - 1) * pageSize;
        const limit = pageSize;

        // Debug: log pagination to understand why offset is always 0
        console.log('[DataProvider] Received pagination:', pagination);
        console.log('[DataProvider] Calculated:', { page, pageSize, offset, limit });

        // Extract consent filters from Refine filters
        const logicalFilters = (filters || []) as LogicalFilter[];
        const consentPrivacy = logicalFilters.find((f) => f.field === "consent_privacy")
          ?.value as boolean | undefined;
        const consentPersonalData = logicalFilters.find(
          (f) => f.field === "consent_personal_data"
        )?.value as boolean | undefined;
        const consentMarketing = logicalFilters.find(
          (f) => f.field === "consent_marketing"
        )?.value as boolean | undefined;

        // Call API using generated service
        const response: UserListResponse = await UsersService.listUsersApiUsersGet({
          offset,
          limit,
          consentPrivacy,
          consentPersonalData,
          consentMarketing,
        });

        return {
          data: response.users,
          total: response.total_count,
        };
      } else if (resource === "transactions") {
        // Transactions require user_id - check both meta and filters
        const userId = (meta as any)?.user_id ||
                      (filters as LogicalFilter[])?.find((f) => f.field === "user_id")?.value;

        if (!userId) {
          throw new Error("user_id filter is required for transactions");
        }

        const logicalFilters = (filters || []) as LogicalFilter[];
        const page = (pagination as any)?.current || 1;
        const pageSize = (pagination as any)?.pageSize || 20;
        const offset = (page - 1) * pageSize;
        const limit = pageSize;

        // Extract additional filters
        const transactionType = logicalFilters.find((f) => f.field === "transaction_type")
          ?.value as string | undefined;
        const dateFrom = logicalFilters.find((f) => f.field === "date_from")?.value as
          | string
          | undefined;
        const dateTo = logicalFilters.find((f) => f.field === "date_to")?.value as
          | string
          | undefined;

        // Call API using generated service
        const response: TransactionListResponse =
          await TransactionsService.getUserTransactionsApiTransactionsUserIdGet({
            userId,
            offset,
            limit,
            transactionType: transactionType as any,
            dateFrom,
            dateTo,
          });

        return {
          data: response.data,
          total: response.pagination.total,
        };
      } else if (resource === "campaigns") {
        // Extract pagination parameters with fallback defaults
        const current = (pagination as any)?.current || 1;
        const pageSize = (pagination as any)?.pageSize || 20;
        const offset = (current - 1) * pageSize;

        // Extract status filter from Refine filters
        const logicalFilters = (filters || []) as LogicalFilter[];
        const statusFilter = logicalFilters.find((f) => f.field === "status")
          ?.value as string | undefined;

        // Call API using generated service
        const response: CampaignListResponse =
          await CampaignsService.listCampaignsApiCampaignsGet({
            offset,
            limit: pageSize,
            status: statusFilter,
          });

        return {
          data: response.campaigns,
          total: response.total_count,
        };
      } else if (resource === "tasks") {
        const endpoint = (meta as any)?.url as string | undefined;
        const isArchived = Boolean((meta as any)?.archived);
        const logicalFilters = (filters || []) as LogicalFilter[];

        const statusFilter = logicalFilters.find((f) => f.field === "status")
          ?.value as string | undefined;
        const errorsOnlyFilter = logicalFilters.find(
          (f) => f.field === "errors_only",
        )?.value as boolean | undefined;
        const dateField = isArchived ? "executed_at" : "created_at";
        const dateFromFilter = logicalFilters.find(
          (f) => "field" in f && f.field === dateField && f.operator === "gte",
        )?.value as string | undefined;
        const dateToFilter = logicalFilters.find(
          (f) => "field" in f && f.field === dateField && f.operator === "lte",
        )?.value as string | undefined;

        const params: Record<string, unknown> = {};

        if (statusFilter && statusFilter !== "all") {
          params.status = statusFilter;
        }

        if (errorsOnlyFilter) {
          params.errors_only = true;
        }

        if (dateFromFilter) {
          params.date_from = dateFromFilter;
        }

        if (dateToFilter) {
          params.date_to = dateToFilter;
        }

        if (!endpoint) {
          throw new Error("Tasks endpoint is required");
        }

        if (isArchived) {
          const current = (pagination as any)?.current || 1;
          const pageSize = (pagination as any)?.pageSize || 50;
          const offset = (current - 1) * pageSize;

          const response: ArchivedTaskListResponse = (
            await axiosInstance.get(endpoint, {
              params: { offset, limit: pageSize, ...params },
            })
          ).data;

          return {
            data: response.tasks,
            total: response.total_count,
          };
        }

        const response: TaskListResponse = (
          await axiosInstance.get(endpoint, {
            params,
          })
        ).data;

        return {
          data: response.tasks,
          total: response.total_count,
        };
      }

      throw new Error(`Resource ${resource} not supported`);
    } catch (error) {
      console.error(`getList error for ${resource}:`, error);
      throw error;
    }
  },

  /**
   * Get single resource by ID.
   *
   * Supports:
   * - users: Get user details by telegram_id
   * - transactions: Not typically used (use getList with user_id filter)
   *
   * @param params - Resource name and ID
   * @returns Promise with single resource data
   */
  getOne: async ({ resource, id }) => {
    try {
      if (resource === "users") {
        // Get user by telegram_id
        const response: UserResponse =
          await UsersService.getUserApiUsersTelegramIdGet({
            telegramId: Number(id),
          });

        return {
          data: response,
        };
      } else if (resource === "campaigns") {
        // Get campaign by ID
        const response = await CampaignsService.getCampaignApiCampaignsCampaignIdGet({
          campaignId: id as string,
        });

        return {
          data: response,
        };
      }

      throw new Error(`Resource ${resource} not supported for getOne`);
    } catch (error) {
      console.error(`getOne error for ${resource} with id ${id}:`, error);
      throw error;
    }
  },

  /**
   * Create new resource.
   *
   * Supports:
   * - transactions: Create manual point adjustment (bonus/redemption)
   * - users: Not implemented in admin panel (users created via bot)
   *
   * @param params - Resource name and data
   * @returns Promise with created resource
   */
  create: async ({ resource, variables, meta }) => {
    try {
      const customUrl = (meta as any)?.url as string | undefined;

      if (customUrl) {
        const response = await axiosInstance.post(customUrl, variables);

        return {
          data: response.data,
        };
      }

      if (resource === "transactions") {
        // Create transaction (manual point adjustment)
        const requestBody = variables as TransactionCreateRequest;

        console.log('[DataProvider] Creating transaction with body:', requestBody);

        // Use axiosInstance directly to ensure cookies are sent
        const response = await axiosInstance.post<TransactionResponse>(
          "/api/transactions",
          requestBody
        );

        console.log('[DataProvider] Transaction created successfully:', response.data);

        return {
          data: response.data,
        };
      } else if (resource === "campaigns") {
        // Create campaign using axiosInstance directly (for proper CSRF token handling)
        console.log('[DataProvider] Creating campaign with body:', variables);
        
        const response = await axiosInstance.post<CampaignResponse>(
          "/api/campaigns",
          variables
        );

        console.log('[DataProvider] Campaign created successfully:', response.data);
        return { data: response.data };
      }

      throw new Error(`Resource ${resource} not supported for create`);
    } catch (error) {
      console.error(`[DataProvider] create error for ${resource}:`, error);
      if (error instanceof AxiosError) {
        console.error('[DataProvider] Response status:', error.response?.status);
        console.error('[DataProvider] Response data:', error.response?.data);
        console.error('[DataProvider] Error code:', error.code);
        console.error('[DataProvider] Error message:', error.message);
        console.error('[DataProvider] Has response:', !!error.response);
        console.error('[DataProvider] Has request:', !!error.request);
      }
      throw error;
    }
  },

  /**
   * Update existing resource.
   *
   * Supports:
   * - users: Update user profile (consents, interests)
   * - campaigns: Update campaign status
   * - transactions: Not supported (transactions are immutable)
   *
   * @param params - Resource name, ID, and update data
   * @returns Promise with updated resource
   */
  update: async ({ resource, id, variables }) => {
    try {
      if (resource === "users") {
        // Update user profile
        const requestBody = variables as UserUpdateRequest;

        const response: UserResponse =
          await UsersService.updateUserApiUsersTelegramIdPut({
            telegramId: Number(id),
            requestBody,
          });

        return {
          data: response,
        };
      } else if (resource === "campaigns") {
        // Edit campaign using PUT (for full campaign editing)
        console.log('[DataProvider] Updating campaign with body:', variables);

        const response = await axiosInstance.put<CampaignResponse>(
          `/api/campaigns/${id}`,
          variables
        );

        console.log('[DataProvider] Campaign updated successfully:', response.data);
        return { data: response.data };
      }

      throw new Error(`Resource ${resource} not supported for update`);
    } catch (error) {
      console.error(`update error for ${resource} with id ${id}:`, error);
      throw error;
    }
  },

  /**
   * Delete resource.
   *
   * Currently not implemented for any resource.
   * Users and transactions should not be deleted via admin panel.
   *
   * @param params - Resource name and ID
   * @returns Promise with deleted resource
   */
  deleteOne: async ({ resource, id }) => {
    try {
      if (resource === "users") {
        // User deletion via admin panel (if needed)
        const response = await UsersService.deleteUserApiUsersTelegramIdDelete({
          telegramId: Number(id),
        });

        return {
          data: { id, ...response },
        };
      } else if (resource === "campaigns") {
        await CampaignsService.deleteCampaignApiCampaignsCampaignIdDelete({
          campaignId: id as string,
        });

        return {
          data: {},
        };
      }

      throw new Error(`Resource ${resource} not supported for deleteOne`);
    } catch (error) {
      console.error(`deleteOne error for ${resource} with id ${id}:`, error);
      throw error;
    }
  },

  /**
   * Get API URL for resource.
   *
   * Used by Refine for URL generation and routing.
   *
   * @param params - Resource name
   * @returns API URL string
   */
  getApiUrl: () => {
    return import.meta.env.VITE_API_URL || "http://localhost:8000";
  },

  /**
   * Custom method for arbitrary API calls.
   *
   * Used by useCustom hook for non-standard API endpoints.
   *
   * @param params - URL, method, and optional payload
   * @returns Promise with response data
   */
  custom: async ({ url, method, payload, query, headers }) => {
    try {
      const response = await axiosInstance({
        url,
        method,
        data: payload,
        params: query,
        headers,
      });

      return {
        data: response.data,
      };
    } catch (error) {
      console.error(`Custom request error for ${method} ${url}:`, error);
      throw error;
    }
  },

  /**
   * Custom search method for universal user search.
   *
   * Implements Requirement 4: Universal search by telegram_id, username, or auth_code.
   * The backend auto-detects search type based on query pattern:
   * - Numeric: telegram_id search
   * - 6 alphanumeric: auth_code search
   * - Other: username fuzzy search
   *
   * @param query - Search query string
   * @param limit - Maximum results to return (default: 10)
   * @returns Promise with search results and metadata
   */
  search: async (query: string, limit: number = 10): Promise<UserSearchResponse> => {
    try {
      // Call search endpoint using axios directly (not in generated service yet)
      const response = await axiosInstance.get<UserSearchResponse>(
        "/api/users/search",
        {
          params: {
            q: query,
            limit,
          },
        }
      );

      return response.data;
    } catch (error) {
      console.error("Search error:", error);

      // Check if it's an axios error with response
      if (error instanceof AxiosError && error.response) {
        throw new Error(
          error.response.data?.detail || "Search failed. Please try again."
        );
      }

      throw new Error("Search failed. Please check your connection.");
    }
  },
} as DataProvider & {
  search: (query: string, limit?: number) => Promise<UserSearchResponse>;
};
